import classNames from 'classnames';
import {
  SubscriptionProductTypes,
  SubscriptionStatusTypes,
  SubscriptionStatusTypesMap,
} from 'config/types';
import { useSubscription } from 'state';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { functions } from 'firebaseSDK';
import { useLocation } from 'react-router-dom';
import openLinkInANewTab from '../../helpers/openLinkInANewTab';
import InfoMessageBlock from '../InfoMessageBlock';
import LoadingPlaceholder from '../LoadingPlaceholder';
import Modal from '../Modal';

const createURLCopy = (location, search) => {
  const globalLocation = window.location;
  const isHashRouter = globalLocation.hash.includes(location.pathname);
  const urlSearchParams = new URLSearchParams(location.search);
  if (search) {
    Object.keys(search).forEach((key) =>
      urlSearchParams.append(key, search[key])
    );
  }
  const pathName = isHashRouter
    ? `${globalLocation.pathname}#${location.pathname}`
    : location.pathname;
  const searchStr = urlSearchParams.toString();
  const searchStrFinal = searchStr ? `?${searchStr}` : '';

  return `${globalLocation.protocol}//${globalLocation.host}${pathName}${searchStrFinal}`;
};

window.createURLCopy = createURLCopy;

/**
 * SubscriptionAction
 *
 * @return {JSX.Element}
 */
const SubscriptionStatus = ({
  className,
  title = 'STATUS',
  freeTrialDescription = 'Tap here to unlock full access to Propa’s property management services.',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState('');
  const location = useLocation();
  const { data } = useSubscription();
  const status = data?.status;

  const checkout = async () => {
    const checkoutConfig = {
      productType: SubscriptionProductTypes.propertyManagement,
      successUrl: createURLCopy(location, { payment: 'success' }),
      cancelUrl: createURLCopy(location, { payment: 'cancel' }),
    };

    const response = await functions.stripe.checkoutUrl(checkoutConfig);

    if (response?.data?.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error('Something went wrong');
    }
  };

  const billing = async () => {
    const response = await functions.stripe.billingPortalUrl();

    if (response?.data?.url) {
      openLinkInANewTab(response.data.url);
    } else {
      throw new Error('Something went wrong');
    }
  };

  const handleClick = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (
        status === SubscriptionStatusTypes.freeTrial ||
        status === SubscriptionStatusTypes.cancelled
      ) {
        await checkout();
      } else {
        await billing();
      }
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }

    setIsLoading(false);
  };

  return (
    <>
      <InfoMessageBlock
        className={classNames(className)}
        caption={title}
        title={SubscriptionStatusTypesMap[status]}
        onClick={handleClick}
        description={
          status === SubscriptionStatusTypes.freeTrial
            ? freeTrialDescription
            : status === SubscriptionStatusTypes.cancelled
            ? 'Tap here to renew your access to Propa’s property management services.'
            : status === SubscriptionStatusTypes.active
            ? 'You currently have an active subscription with Propa. Tap here to manage your subscription.'
            : 'Your subscription is unpaid'
        }
        blue={status === SubscriptionStatusTypes.freeTrial}
        yellow={status === SubscriptionStatusTypes.cancelled}
        green={status === SubscriptionStatusTypes.active}
      />
      {isLoading ? (
        <Modal transparent>
          <LoadingPlaceholder />
        </Modal>
      ) : null}
    </>
  );
};

SubscriptionStatus.displayName = 'SubscriptionStatus';

export default observer(SubscriptionStatus);
